import './header.scss';

import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { colors, formatVND } from 'app/shared/theme/colors';
import { LanguageSwitcher, useTranslation } from 'app/shared/i18n';

export interface IHeaderProps {
  isAuthenticated: boolean;
  isAdmin: boolean;
  ribbonEnv: string;
  isInProduction: boolean;
  isOpenAPIEnabled: boolean;
  username: string;
  balance: number;
  rank: any;
  setShowDropdown: (show: boolean) => void;
  setShowDropdownAuth: (show: boolean) => void;
}

const Header = (props: IHeaderProps) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    if (userMenuOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userMenuOpen]);

  return (
    <div id="app-header">
      <div className="ms-header">
        <Link to="/" className="ms-logo" onClick={() => setMobileOpen(false)}>
          <div className="ms-logo__icon">M</div>
          <div className="ms-logo__text">
            Mails<span>Ngon</span>
          </div>
        </Link>

        <nav className="ms-nav">
          <NavLink to="/" end className={({ isActive }) => (isActive ? 'active' : '')}>
            {t('header.home')}
          </NavLink>
          {props.isAuthenticated && (
            <>
              <NavLink to="/deposit" className={({ isActive }) => (isActive ? 'active' : '')}>
                {t('header.deposit')}
              </NavLink>
              <NavLink to="/order" className={({ isActive }) => (isActive ? 'active' : '')}>
                {t('header.order')}
              </NavLink>
            </>
          )}
          <NavLink to="/check-live-mail" className={({ isActive }) => (isActive ? 'active' : '')}>
            {t('header.tools')}
          </NavLink>
          <NavLink to="/membership" className={({ isActive }) => (isActive ? 'active' : '')} style={{ color: colors.accent }}>
            🎁 {t('header.promotions')}
          </NavLink>
          <a href="/content/api-docs.html" target="_blank" rel="noreferrer">
            {t('header.apiDocs')}
          </a>
        </nav>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {props.isAuthenticated ? (
            <>
              <LanguageSwitcher />
              <Link to="/deposit" className="ms-balance">
                <span className="ms-balance__label">{t('header.balance')}</span>
                <span className="ms-balance__value">{formatVND(props.balance ?? 0)}</span>
              </Link>
              <div ref={dropdownRef} style={{ position: 'relative' }}>
                <div
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    background: colors.surface,
                    border: `1px solid ${userMenuOpen ? colors.accent : colors.border}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 14,
                    cursor: 'pointer',
                    transition: 'border-color .2s',
                  }}
                >
                  👤
                </div>
                {userMenuOpen && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 44,
                      right: 0,
                      zIndex: 999,
                      width: 220,
                      background: colors.bgCard,
                      border: `1px solid ${colors.border}`,
                      borderRadius: 12,
                      boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
                      overflow: 'hidden',
                      animation: 'fadeIn .15s ease',
                    }}
                  >
                    <div
                      style={{
                        padding: '14px 16px',
                        borderBottom: `1px solid ${colors.border}`,
                        background: `linear-gradient(135deg, ${colors.surface}, ${colors.bgCard})`,
                      }}
                    >
                      <div style={{ fontSize: 14, fontWeight: 700, color: colors.text }}>{props.username}</div>
                      <div
                        style={{
                          marginTop: 6,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 5,
                          padding: '3px 10px',
                          borderRadius: 6,
                          background: 'linear-gradient(135deg, rgba(245,158,11,0.15), rgba(249,115,22,0.1))',
                          border: '1px solid rgba(245,158,11,0.25)',
                        }}
                      >
                        <span style={{ fontSize: 12 }}>👑</span>
                        <span style={{ fontSize: 12, fontWeight: 700, color: colors.accent }}>
                          {props.rank?.enabled !== false ? props.rank?.name || 'Member' : 'Member'}
                        </span>
                        {props.rank?.enabled !== false && props.rank?.discountPercent > 0 && (
                          <span style={{ fontSize: 10, color: colors.success, fontWeight: 600 }}>-{props.rank.discountPercent}%</span>
                        )}
                      </div>
                    </div>
                    {[
                      { to: '/profile', icon: '👤', label: t('header.userMenu.profile') },
                      { to: '/membership', icon: '👑', label: t('header.userMenu.membership') },
                      { to: '/deposit', icon: '💰', label: t('header.userMenu.deposit') },
                      { to: '/order', icon: '🛒', label: t('header.userMenu.order') },
                    ].map(item => (
                      <Link
                        key={item.to}
                        to={item.to}
                        onClick={() => setUserMenuOpen(false)}
                        style={{
                          display: 'block',
                          padding: '10px 16px',
                          fontSize: 13,
                          color: colors.muted,
                          textDecoration: 'none',
                          transition: 'all .1s',
                        }}
                        onMouseEnter={e => {
                          (e.currentTarget as HTMLElement).style.background = colors.surface;
                          (e.currentTarget as HTMLElement).style.color = colors.text;
                        }}
                        onMouseLeave={e => {
                          (e.currentTarget as HTMLElement).style.background = 'transparent';
                          (e.currentTarget as HTMLElement).style.color = colors.muted;
                        }}
                      >
                        {item.icon} {item.label}
                      </Link>
                    ))}
                    <div style={{ borderTop: `1px solid ${colors.border}` }}>
                      <Link
                        to="/logout"
                        onClick={() => setUserMenuOpen(false)}
                        style={{
                          display: 'block',
                          padding: '10px 16px',
                          fontSize: 13,
                          color: colors.danger,
                          textDecoration: 'none',
                          transition: 'all .1s',
                        }}
                        onMouseEnter={e => {
                          (e.currentTarget as HTMLElement).style.background = colors.dangerDim;
                        }}
                        onMouseLeave={e => {
                          (e.currentTarget as HTMLElement).style.background = 'transparent';
                        }}
                      >
                        🚪 {t('header.logout')}
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <LanguageSwitcher />
              <Link to="/login" className="ms-btn ms-btn--ghost" style={{ padding: '7px 16px', fontSize: 13 }}>
                {t('header.login')}
              </Link>
              <Link to="/account/register" className="ms-btn ms-btn--primary" style={{ padding: '7px 16px', fontSize: 13 }}>
                {t('header.register')}
              </Link>
            </>
          )}
          <button
            className="ms-mobile-toggle"
            onClick={() => setMobileOpen(!mobileOpen)}
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: colors.surface,
              border: `1px solid ${colors.border}`,
              color: colors.text,
              fontSize: 16,
              cursor: 'pointer',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {mobileOpen ? '✕' : '☰'}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="ms-mobile-menu">
          <Link to="/" onClick={() => setMobileOpen(false)} className="ms-mobile-menu__item">
            {t('header.home')}
          </Link>
          {props.isAuthenticated ? (
            <>
              <Link to="/profile" onClick={() => setMobileOpen(false)} className="ms-mobile-menu__item">
                {t('header.userMenu.profile')}
              </Link>
              <Link to="/deposit" onClick={() => setMobileOpen(false)} className="ms-mobile-menu__item">
                {t('header.deposit')}
              </Link>
              <Link to="/order" onClick={() => setMobileOpen(false)} className="ms-mobile-menu__item">
                {t('header.order')}
              </Link>
            </>
          ) : null}
          <Link to="/check-live-mail" onClick={() => setMobileOpen(false)} className="ms-mobile-menu__item">
            {t('header.tools')}
          </Link>
          <Link to="/membership" onClick={() => setMobileOpen(false)} className="ms-mobile-menu__item" style={{ color: colors.accent }}>
            🎁 {t('header.promotions')}
          </Link>
          {!props.isAuthenticated ? (
            <>
              <Link to="/login" onClick={() => setMobileOpen(false)} className="ms-mobile-menu__item">
                {t('header.login')}
              </Link>
              <Link to="/account/register" onClick={() => setMobileOpen(false)} className="ms-mobile-menu__item">
                {t('header.register')}
              </Link>
            </>
          ) : (
            <Link to="/logout" onClick={() => setMobileOpen(false)} className="ms-mobile-menu__item" style={{ color: colors.danger }}>
              {t('header.logout')}
            </Link>
          )}
        </div>
      )}
    </div>
  );
};

export default Header;
